/*
 * This file is part of glowingblue/orell-articles.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Page from 'flarum/common/components/Page';
import type { IPageAttrs } from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import ItemList from 'flarum/common/utils/ItemList';
import LinkButton from 'flarum/common/components/LinkButton';
import ArticleHero from './ArticleHero';
import ArticleInfos from './ArticleInfos';
import type Article from '../models/Article';
import { components } from '@glowingblue-orell-thalia-common';
import * as terms from '@fof-terms';
import app from 'flarum/forum/app';
import type Mithril from 'mithril';
const ExpandableContent = components.ExpandableContent;
const UpdateAlert = terms.components.UpdateAlert;

export default class ArticlePage extends Page {
	article: Article | null = null;

	oninit(vnode: Mithril.Vnode<IPageAttrs, this>) {
		super.oninit(vnode);

		app.history.push('of_article', '');

		this.refresh();

		this.bodyClass = 'App--of_article';
	}

	view() {
		const { article } = this;

		return (
			<div className="ArticlePage">
				<UpdateAlert />
				{article && [
					// Invisible duplicate: on tablet the real hero is absolutely positioned,
					// so this one holds the space it would otherwise occupy.
					<ArticleHero helperElement={true} article={article} articleDetailsItems={this.articleDetailsItems()} />,

					<ArticleHero article={article} articleDetailsItems={this.articleDetailsItems()} />,
				]}
				<div className="container">
					<div className="sideNavContainer">
						<div className="ArticlePage-Details--wrapper sideNav"></div>
						<div className="ArticlePage-content--wrapper sideNavOffset">
							{article ? this.items().toArray() : <LoadingIndicator className="LoadingIndicator--block" />}
						</div>
						<div className="ArticlePage-rightSide">{this.rightSideItems().toArray()}</div>
					</div>
				</div>
			</div>
		);
	}

	items(): ItemList<Mithril.Children> {
		const { article } = this;

		const items = new ItemList<Mithril.Children>();

		if (!article) {
			return items;
		}

		if (article.description()) {
			items.add(
				'description',
				<ExpandableContent
					body={[<div className="ArticlePage-desc">{m.trust(article.description())}</div>, <ArticleInfos article={article} />]}
					footer={<span>{app.translator.trans('glowingblue-orell-articles.forum.article.read_more')}</span>}
					isExpandable={true}
					collapsedHeigh={171}
				/>,
				80
			);
		}
		if (article.isAvailable() && article.shopLink()) {
			items.add(
				'shop-link',
				<div className="ArticlePage-toShop">
					<LinkButton
						href={article.shopLink()}
						className="LinkButton"
						target="_blank"
						config={() => {}} // keeps Link from routing the external URL through m.route
					>
						{app.translator.trans('glowingblue-orell-articles.forum.article.shopLink')}
					</LinkButton>
				</div>,
				60
			);
		}

		return items;
	}

	rightSideItems(): ItemList<Mithril.Children> {
		const items = new ItemList<Mithril.Children>();
		return items;
	}

	articleDetailsItems(): ItemList<Mithril.Children> {
		const { article } = this;

		const items = new ItemList<Mithril.Children>();

		if (article) {
			items.add('details', <ArticleInfos article={article} />);
		}

		return items;
	}

	async refresh(): Promise<void> {
		this.article = null;

		const preloadedArticle = app.preloadedApiDocument<Article>();
		if (preloadedArticle) {
			// We must wrap this in a setTimeout because if we are mounting this
			// component for the first time on page load, then any calls to m.redraw
			// will be ineffective.
			setTimeout(() => this.show(preloadedArticle), 0);
		} else {
			// A search result we hold no row for yet: the route carries the EAN as its
			// id, e.g. `0000000000000-the-slug`.
			const matches = m.route.param('id').match(/([0-9]{13})/);
			if (matches) {
				const ean = matches[0];

				let article: Article = app.store.createRecord<Article>('of_articles');

				try {
					article = await article.save({ ean });
				} catch (e) {
					// Shop no longer carries this EAN, so there is nothing to route to.
					m.redraw();

					return;
				}

				// The 3rd parameter tells Mithril to replace the current window.history
				// entry instead of inserting one, so the back button does not land on the
				// route where the ean was used.
				m.route.set(app.route.of_article(article), null, true);
			} else {
				this.show(await app.store.find<Article>('of_articles', m.route.param('id')));
			}
		}

		m.redraw();
	}

	show(article: Article) {
		this.article = article;

		app.setTitle(article.title());

		m.redraw();
	}
}
